import { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { ShoppingCart, Send, PackageCheck, Edit, Loader2, RefreshCcw, Bell, MessageSquare, UserPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import ReceiveOrderDialog from "../components/inventory/ReceiveOrderDialog";
import OrderMessagesDialog from "../components/orders/OrderMessagesDialog";

const STATUS_GROUPS = {
  drafts:   ["draft"],
  pending:  ["sent", "pending", "confirmed"],
  received: ["received"],
};

const STATUS_LABEL = {
  draft: "Borrador",
  sent: "Enviado",
  pending: "Pendiente",
  confirmed: "Confirmado",
  received: "Recibido",
};

const STATUS_COLOR = {
  draft: "bg-amber-100 text-amber-800",
  sent: "bg-blue-100 text-blue-800",
  pending: "bg-blue-100 text-blue-800",
  confirmed: "bg-indigo-100 text-indigo-800",
  received: "bg-emerald-100 text-emerald-800",
};

const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "—";

export default function OrdersPage() {
  const { currentRestaurant } = useOutletContext();
  const [tab, setTab] = useState("drafts");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [receiveOrder, setReceiveOrder] = useState(null);
  const [thread, setThread] = useState(null);
  const [assigning, setAssigning] = useState(null); // { order_id } cuando hay dialog abierto
  const [suppliers, setSuppliers] = useState([]);
  const [supplierChoice, setSupplierChoice] = useState("");
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // {supplier_id, supplier_name, reason}

  const openAssignSupplier = useCallback(async (order) => {
    setAssigning(order);
    setSupplierChoice("");
    setSuggestion(null);
    try {
      const [list, sug] = await Promise.all([
        api.getSuppliers(currentRestaurant.restaurant_id),
        api.request(`/orders/${order.order_id}/supplier-suggestion`).catch(() => null),
      ]);
      setSuppliers(Array.isArray(list) ? list : []);
      if (sug?.supplier_id) {
        setSuggestion(sug);
        setSupplierChoice(sug.supplier_id); // auto-select
      }
    } catch {
      setSuppliers([]);
    }
  }, [currentRestaurant?.restaurant_id]);

  const saveSupplier = async () => {
    if (!assigning || !supplierChoice) return;
    setSavingSupplier(true);
    try {
      await api.request(`/orders/${assigning.order_id}`, {
        method: "PATCH",
        body: JSON.stringify({ supplier_id: supplierChoice }),
      });
      toast.success("Proveedor asignado");
      setAssigning(null);
      setSupplierChoice("");
      await load();
    } catch (e) {
      toast.error(e?.message || "Error asignando proveedor");
    } finally {
      setSavingSupplier(false);
    }
  };

  const load = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    try {
      const data = await api.getOrders(currentRestaurant.restaurant_id);
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      toast.error("No se pudieron cargar los pedidos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentRestaurant?.restaurant_id]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const acc = { drafts: [], pending: [], received: [] };
    for (const o of orders) {
      const status = o.status || "draft";
      for (const [g, statuses] of Object.entries(STATUS_GROUPS)) {
        if (statuses.includes(status)) acc[g].push(o);
      }
    }
    return acc;
  }, [orders]);

  const handleSend = async (orderId) => {
    setSendingId(orderId);
    try {
      await api.request(`/orders/${orderId}/send`, { method: "POST" });
      toast.success("Pedido enviado al proveedor");
      await load();
    } catch (e) {
      toast.error(e?.message || "No se pudo enviar el pedido");
    } finally {
      setSendingId(null);
    }
  };

  const handleRemind = async (orderId) => {
    if (!currentRestaurant?.restaurant_id) return;
    setRemindingId(orderId);
    try {
      const res = await api.request(
        `/restaurants/${currentRestaurant.restaurant_id}/orders/${orderId}/send-reminder`,
        { method: "POST" },
      );
      toast.success(`Recordatorio enviado a ${res?.recipient || "el proveedor"}`);
      await load();
    } catch (e) {
      const msg = e?.message || "No se pudo enviar el recordatorio";
      toast.error(msg);
    } finally {
      setRemindingId(null);
    }
  };

  const renderTable = (rows, group) => {
    if (rows.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {group === "drafts" && "No hay borradores. Cuando un ingrediente caiga bajo mínimos, MenuGenius creará un borrador automáticamente."}
            {group === "pending" && "No hay pedidos pendientes de recibir."}
            {group === "received" && "Aún no has recibido ningún pedido."}
          </CardContent>
        </Card>
      );
    }
    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                  <TableCell>
                    {o.supplier_name ? (
                      <p className="font-medium text-sm">{o.supplier_name}</p>
                    ) : group === "drafts" ? (
                      <button
                        onClick={() => openAssignSupplier(o)}
                        data-testid={`order-assign-supplier-${o.order_id}`}
                        className="font-medium text-sm text-amber-700 hover:text-amber-900 hover:underline inline-flex items-center gap-1.5 cursor-pointer"
                        title="Asignar proveedor a este borrador"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Sin proveedor — Asignar
                      </button>
                    ) : (
                      <p className="font-medium text-sm text-muted-foreground italic">Sin proveedor</p>
                    )}
                    <p className="text-xs text-muted-foreground">#{(o.order_id || "").slice(-8)}</p>
                  </TableCell>
                  <TableCell className="text-center">{(o.items || []).length}</TableCell>
                  <TableCell className="text-right font-medium">{fmtEUR(o.total_amount)}</TableCell>
                  <TableCell className="text-xs">
                    {group === "received"
                      ? fmtDate(o.received_date)
                      : fmtDate(o.sent_at || o.created_at)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_COLOR[o.status] || ""} text-[10px] font-normal`}>
                      {STATUS_LABEL[o.status] || o.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {group === "drafts" && (
                      <div className="flex justify-end gap-2">
                        <Link to="/app/inventory">
                          <Button variant="outline" size="sm" data-testid={`order-edit-${o.order_id}`}>
                            <Edit className="w-3.5 h-3.5 mr-1.5" /> Revisar
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          onClick={() => handleSend(o.order_id)}
                          disabled={sendingId === o.order_id}
                          data-testid={`order-send-${o.order_id}`}
                        >
                          {sendingId === o.order_id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Enviar
                        </Button>
                      </div>
                    )}
                    {group === "pending" && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setThread(o)}
                          data-testid={`order-thread-${o.order_id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                          {(o.messages_count || 0) > 0 ? `Mensajes (${o.messages_count})` : "Mensajes"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemind(o.order_id)}
                          disabled={remindingId === o.order_id}
                          title={o.reminder_count ? `Recordatorio ya enviado ${o.reminder_count}× — cooldown del config aplica` : "Enviar recordatorio amistoso al proveedor"}
                          data-testid={`order-remind-${o.order_id}`}
                        >
                          {remindingId === o.order_id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Bell className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Recordar
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setReceiveOrder(o)}
                          data-testid={`order-receive-${o.order_id}`}
                        >
                          <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Recibir
                        </Button>
                      </div>
                    )}
                    {group === "received" && (
                      <span className="text-xs text-muted-foreground">
                        {o.received_date ? `Recibido ${fmtDate(o.received_date)}` : "Recibido"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center flex-shrink-0">
            <ShoppingCart className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Pedidos</h1>
            <p className="text-sm text-muted-foreground">
              Revisa borradores, envía pedidos a tus proveedores y registra recepciones.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          data-testid="orders-refresh-btn"
        >
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></CardContent></Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="drafts" data-testid="orders-tab-drafts">
              Borradores
              {grouped.drafts.length > 0 && (
                <Badge variant="secondary" className="ml-2">{grouped.drafts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="orders-tab-pending">
              Enviados
              {grouped.pending.length > 0 && (
                <Badge variant="secondary" className="ml-2">{grouped.pending.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="received" data-testid="orders-tab-received">
              Recibidos
              {grouped.received.length > 0 && (
                <Badge variant="secondary" className="ml-2">{grouped.received.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="drafts" className="mt-5">{renderTable(grouped.drafts, "drafts")}</TabsContent>
          <TabsContent value="pending" className="mt-5">{renderTable(grouped.pending, "pending")}</TabsContent>
          <TabsContent value="received" className="mt-5">{renderTable(grouped.received, "received")}</TabsContent>
        </Tabs>
      )}

      <ReceiveOrderDialog
        open={!!receiveOrder}
        onOpenChange={(o) => { if (!o) setReceiveOrder(null); }}
        order={receiveOrder}
        onSuccess={() => { setReceiveOrder(null); load(); }}
      />
      <OrderMessagesDialog
        open={!!thread}
        onOpenChange={(o) => { if (!o) setThread(null); }}
        restaurantId={currentRestaurant?.restaurant_id}
        order={thread}
      />

      {/* Assign supplier dialog (for drafts without supplier) */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="max-w-md" data-testid="assign-supplier-dialog">
          <DialogHeader>
            <DialogTitle>Asignar proveedor</DialogTitle>
            <DialogDescription>
              Selecciona el proveedor al que enviarás este pedido.
              {assigning && <span className="block text-xs mt-1 opacity-70">Pedido #{(assigning.order_id || "").slice(-8)} · {(assigning.items || []).length} ítems · {fmtEUR(assigning.total_amount)}</span>}
            </DialogDescription>
          </DialogHeader>
          {suppliers.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No tienes proveedores registrados.
              <Link to="/app/suppliers" className="block mt-2 text-primary underline">Añadir proveedor</Link>
            </div>
          ) : (
            <>
              {suggestion?.supplier_id && (
                <div data-testid="supplier-suggestion-hint" className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-emerald-900">
                      Recomendado: {suggestion.supplier_name}
                    </div>
                    <div className="text-xs text-emerald-700/90">{suggestion.reason}</div>
                  </div>
                </div>
              )}
              <Select value={supplierChoice} onValueChange={setSupplierChoice}>
                <SelectTrigger data-testid="assign-supplier-select">
                  <SelectValue placeholder="Seleccionar proveedor…" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.supplier_id} value={s.supplier_id}>
                      {s.name}{s.supplier_id === suggestion?.supplier_id ? " · ✨ Recomendado" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssigning(null)}>Cancelar</Button>
            <Button onClick={saveSupplier} disabled={!supplierChoice || savingSupplier} data-testid="assign-supplier-save">
              {savingSupplier && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
